import { useQuery, useMutation } from "@apollo/client"
import { useParams, Link, useLocation } from "react-router-dom"
import classNames from "classnames"
import Helmet from "react-helmet"
import { DateTime } from "luxon"
import toUpper from "lodash/toUpper"
import get from "lodash/get"
import findKey from "lodash/findKey"
import isEmpty from "lodash/isEmpty"
import { useEffect, useState } from "react"

import Hero from "components/shared/Hero"
import Card from "components/shared/Card"
import FullPageLoader from "components/shared/FullPageLoader"
import { generateRedirectUrl, avoidPreRedirectUrl } from "shared/helpers/url"
import ErrorToast from "components/shared/ErrorToast"
import { ReadCurrentAccountPromoCodes } from "components/AccountPromoCodes/operations"
import {
  useForceAuthEffect,
  useLoginState,
  useVerificationRedirectEffect
} from "components/Login/hooks"
import { errorsVar } from "cache"
import { useTranslation } from "react-i18next"
import { formatSessionDate } from "shared/formatting/datetime"
import { isCancellationAllowed } from "shared/helpers/cancelation"
import { trackEvent } from "shared/analytics"

import ToggleButton from "components/shared/ToggleButton"
import clock from "./assets/clock.svg"
import mapPin from "./assets/mapPin.svg"
import receipt from "./assets/receipt.svg"
import directions from "./assets/directions.svg"
import vehicle from "./assets/vehicle.svg"
import notification from "./assets/notification.svg"
import {
  CancelParkingSession,
  GetParkingSession,
  UpdateSmsNotificationPreference
} from "./operations"
import ParkingInstructionsCard from "./ParkingInstructionsCard"

import styles from "./ParkingSession.module.scss"

const googleMapsUrl = ({ lat, lon }) =>
  `https://maps.google.com/maps?saddr=Current Location&daddr=${lat},${lon}`

const ParkingSession = () => {
  useForceAuthEffect()
  useVerificationRedirectEffect()
  const [isLoggedIn] = useLoginState()
  const [t] = useTranslation()
  const [isChecked, setIsChecked] = useState(false)

  const { parkingSessionId } = useParams()
  const location = useLocation()
  const isNewPurchase = location.search.includes("purchased")
  const isPlateUpdated = location.search.includes("plate-updated")
  const { loading, data, error } = useQuery(GetParkingSession, {
    variables: { id: parkingSessionId },
    skip: !isLoggedIn
  })

  const { data: currentCodesData } = useQuery(ReadCurrentAccountPromoCodes, {
    variables: { rsvpPortalId: process.env.REACT_APP_RSVP_PORTAL_ID }
  })

  const id = get(data, "parkingSession.id")
  const rateName = get(data, "parkingSession.rateName")
  const plateNumber = get(data, "parkingSession.plateNumber")
  const startTime = get(data, "parkingSession.startTime")
  const endTime = get(data, "parkingSession.endTime")
  const numAllowedPlateSwitches = get(
    data,
    "parkingSession.numAllowedPlateSwitches"
  )
  const invoiceNumber = get(
    data,
    "parkingSession.transactionHistory.invoiceNumber"
  )
  const txnId = get(data, "parkingSession.transactionHistory.id")
  const address = get(data, "parkingSession.zone.address")
  const entranceLocation = get(data, "parkingSession.zone.entranceLocation")
  const timezone = get(data, "parkingSession.zone.timezone")
  const promoCode = get(data, "parkingSession.promoCode")
  const isCancelled = get(data, "parkingSession.voided")
  const isSmsNotificationEnabled = get(
    data,
    "parkingSession.zoneSmsNotificationEnabled"
  )

  const [cancelSession, { loading: cancelling }] = useMutation(
    CancelParkingSession,
    {
      onCompleted: ({ cancelParkingSession }) => {
        const { errors } = cancelParkingSession
        if (!isEmpty(errors)) {
          errorsVar(errors)
        } else {
          errorsVar(null)

          trackEvent("cancel_reservation", {
            transaction_id: invoiceNumber,
            rate_name: rateName,
            location: address?.street
          })
        }
      }
    }
  )

  const [updateSmsPreference, { loading: updatingSmsPreference }] = useMutation(
    UpdateSmsNotificationPreference,
    {
      onCompleted: ({ updateSmsNotificationPreference }) => {
        const { errors } = updateSmsNotificationPreference
        if (!isEmpty(errors)) {
          errorsVar(errors)
        }
      }
    }
  )

  const handleSmsPreferenceChange = (value) => {
    setIsChecked(value)
    updateSmsPreference({
      variables: {
        input: {
          parkingSessionId,
          notificationEnabled: value
        }
      }
    })
  }

  // Track purchase completion and plate updates
  useEffect(() => {
    if (!loading && data) {
      const eventMap = {
        purchase: isNewPurchase,
        plate_updated: isPlateUpdated
      }
      setIsChecked(data.parkingSession.notificationEnabled)
      const eventName = findKey(eventMap, Boolean)

      if (eventName) {
        trackEvent(eventName, {
          transaction_id: invoiceNumber,
          location: address?.street
        })
      }
    }
  }, [loading, data, isNewPurchase, isPlateUpdated, invoiceNumber, address])

  if (!isLoggedIn || loading)
    return <FullPageLoader message="Loading parking reservation" />
  if (cancelling)
    return <FullPageLoader message="Cancelling parking reservation" />
  if (updatingSmsPreference)
    return <FullPageLoader message="Updating reminder preference" />
  if (error) return <ErrorToast />

  // TODO - maybe a selector/field policy?
  const { lat: addressLat, lon: addressLon } = address || {}
  const { lat: entranceLat, lon: entranceLon } = entranceLocation || {}

  let coords = { lat: addressLat, lon: addressLon }
  if (entranceLat && entranceLon) {
    coords = { lat: entranceLat, lon: entranceLon }
  }

  const pageTitle = loading
    ? "Loading Parking Reservation..."
    : `Parking Reservation ${invoiceNumber.toUpperCase()}`

  const renderStatus = () => {
    if (isCancelled) {
      return (
        <Card className={classNames("d-flex px-3 py-2 mb-3", styles.callout)}>
          <div className="pe-3">
            <i className="fs-1 text-primary bi bi-exclamation-circle" />
          </div>
          <div className="d-flex align-items-center fw-bold">
            This parking reservation has been cancelled.
          </div>
        </Card>
      )
    }

    return null
  }

  const cancel = () => {
    cancelSession({
      variables: { input: { id: parkingSessionId } }
    })
  }

  const renderModifiers = () => {
    const isExpired =
      DateTime.fromISO(endTime).setZone(timezone) <
      DateTime.now().setZone(timezone)

    if (isExpired || isCancelled) {
      return null
    }

    // 0 means no switches allowed
    // 1 means you can only register 1 plate, which is your current one
    // -1 means infinite switches
    const switchingAllowed =
      numAllowedPlateSwitches > 1 || numAllowedPlateSwitches === -1

    // If switching is allowed, show the change plate button
    const showChangePlateButton = switchingAllowed && !isCancelled

    const countExplanation =
      numAllowedPlateSwitches === -1
        ? "There is no limit on the number of vehicle plates that can be stored in your account."
        : `Up to ${numAllowedPlateSwitches} vehicle plates may be stored in your account.`

    const cancellationAllowed = isCancellationAllowed(startTime, timezone)

    return (
      <div className={styles.modifierWrapper}>
        <div className={styles.changeReservationCopy}>
          {showChangePlateButton && (
            <div>
              {countExplanation}
              <br />
              Please ensure that you have selected the actual vehicle plate that
              will be parking at {address.street},&nbsp;
              {address.city}.
            </div>
          )}
          <br />
          <span
            dangerouslySetInnerHTML={{
              __html: t("parkingSession.cancellation")
            }}
          />
        </div>
        <div className={styles.modifiers}>
          {showChangePlateButton && (
            <Link
              to={generateRedirectUrl(`/sessions/${id}/change-vehicle`, {
                destUrl: `${process.env.REACT_APP_RSVP_BASE}/parking-reservation/${parkingSessionId}?plate-updated`,
                originUrl: `${process.env.REACT_APP_RSVP_BASE}/consume/parking-reservation/${parkingSessionId}`
              })}
              className={classNames(
                "btn",
                "btn-outline-dark",
                "fw-bold",
                styles.modifierButton
              )}
              target="_blank"
            >
              Change Plate
            </Link>
          )}
          <button
            className={classNames(
              "btn",
              "btn-outline-dark",
              "fw-bold",
              styles.modifierButton
            )}
            type="button"
            onClick={cancel}
            disabled={!cancellationAllowed}
          >
            Cancel Parking Reservation
          </button>
        </div>
      </div>
    )
  }

  const hasPromoCodes = !isEmpty(
    get(currentCodesData, "currentAccountPromoCodes")
  )
  const anotherPurchaseUrl = hasPromoCodes
    ? "/parking-codes"
    : "/select-parking"

  return (
    <div className={styles.page}>
      <Helmet title={pageTitle} />
      <div className={styles.hero}>
        <Hero>
          <Hero.Headline className={styles.headline}>
            Current Vehicle
          </Hero.Headline>
          <div className={styles.plate}>{plateNumber}</div>
          <div className="container-sm">
            <div className="row justify-content-center">
              <div className="col-md-8 col-xl-6">{renderModifiers()}</div>
            </div>
          </div>
        </Hero>
      </div>
      <div className="container-sm">
        <div className="row justify-content-center">
          <div className="col-md-8 col-xl-6">
            <div className={styles.content}>
              {renderStatus()}
              {isSmsNotificationEnabled && (
                <Card className={classNames(styles.card, styles.detailsCard)}>
                  <div className={styles.reminderCard}>
                    <div>
                      <img src={notification} alt="notification" />
                    </div>
                    <div className={styles.reminderCardText}>
                      Send me text reminders
                    </div>
                    <div className={styles.reminderCardToggle}>
                      <ToggleButton
                        isChecked={isChecked}
                        onChange={handleSmsPreferenceChange}
                      />
                    </div>
                  </div>
                </Card>
              )}
              {promoCode && (
                <Card
                  className={classNames(
                    styles.card,
                    styles.detailsCard,
                    styles.promoCard
                  )}
                >
                  <div>
                    <div className={styles.detailsHeader}>Description</div>
                    <div className={styles.detailsCopy}>
                      {promoCode.shortDesc}
                    </div>
                  </div>
                  <div>
                    <div className={styles.detailsHeader}>Parking Code</div>
                    <div className={styles.detailsCopy}>
                      {toUpper(promoCode.redeemCode)}
                    </div>
                  </div>
                </Card>
              )}
              <Card className={classNames(styles.card, styles.detailsCard)}>
                <div className={styles.detailsGrid}>
                  <img src={clock} alt="clock" />
                  <div>
                    <div className={styles.detailsHeader}>Date</div>
                    <div className={styles.detailsCopy}>
                      {formatSessionDate(startTime, {
                        timezone,
                        format: DateTime.DATETIME_MED
                      })}{" "}
                      -{" "}
                      {formatSessionDate(endTime, {
                        timezone,
                        format: DateTime.DATETIME_MED
                      })}
                    </div>
                  </div>
                  {!isEmpty(rateName) && (
                    <>
                      <img src={vehicle} alt="clock" />
                      <div>
                        <div className={styles.detailsHeader}>Parking Rate</div>
                        <div className={styles.detailsCopy}>{rateName}</div>
                      </div>
                    </>
                  )}
                  <img src={mapPin} alt="map" />
                  <div>
                    <div className={styles.detailsHeader}>Location</div>
                    <div className={styles.detailsCopy}>
                      {address.street},&nbsp;
                      {address.city}
                    </div>
                    <a
                      href={googleMapsUrl(coords)}
                      className={styles.detailsSmallCopy}
                    >
                      <img src={directions} alt="directions" />
                      &nbsp; View Directions
                    </a>
                  </div>
                  <img src={receipt} alt="receipt" />
                  <div>
                    <div className={styles.detailsHeader}>Receipt #</div>
                    <div className={styles.detailsCopy}>
                      {toUpper(invoiceNumber)}
                    </div>
                    <Link
                      to={avoidPreRedirectUrl(`/receipts/${txnId}`, {
                        destUrl: `${process.env.REACT_APP_RSVP_BASE}`
                      })}
                      target="_blank"
                      className={styles.detailsSmallCopy}
                    >
                      View Receipt
                    </Link>
                  </div>
                </div>
              </Card>
              <ParkingInstructionsCard />
              <div className="d-grid">
                <Link
                  to={anotherPurchaseUrl}
                  className="btn btn-primary btn-lg fw-bold"
                >
                  Reserve Another Parking Spot
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ErrorToast />
    </div>
  )
}

export default ParkingSession
